// "Set your GSTIN / state" banner (gst-parked task 030, owner decision D4).
// Shown on Business Settings and the dashboard; never blocks anything.

import { Link } from 'react-router-dom';
import { gstSetupText, type GstSetupWarning } from '@/lib/gst-setup';

interface GstSetupBannerProps {
  warning: GstSetupWarning | null;
  /** OWNER+ get links to fix it; everyone else is told to ask the owner. */
  canFix: boolean;
  /** On Business Settings the GSTIN / State fields are right below, so no link to them. */
  onSettingsPage?: boolean;
}

export function GstSetupBanner({ warning, canFix, onSettingsPage = false }: GstSetupBannerProps) {
  if (!warning) return null;
  const { title, lines } = gstSetupText(warning);
  const needsBusiness = warning.kind === 'UNREGISTERED_NO_GSTIN' || warning.gstin || warning.state;
  const needsStores = warning.kind === 'MISSING' && warning.locations.length > 0;
  const tone =
    warning.kind === 'MISSING' ? 'from-warning-500 to-danger-500' : 'from-brand-500 to-indigo-500';

  return (
    <div
      role="status"
      data-testid="gst-setup-banner"
      className="mb-6 rounded-2xl border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-neutral-800 p-4 shadow-md animate-fade-in flex gap-3"
    >
      <div
        className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center text-white shadow-sm`}
        aria-hidden="true"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-neutral-900 dark:text-white">{title}</p>
        {lines.map((line) => (
          <p key={line} className="text-xs text-neutral-600 dark:text-neutral-300 mt-0.5">
            {line}
          </p>
        ))}
        {canFix ? (
          <div className="flex flex-wrap gap-3 mt-2">
            {needsBusiness && !onSettingsPage && (
              <Link
                to="/settings"
                className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline transition-all duration-200"
              >
                Add GSTIN and State
              </Link>
            )}
            {needsStores && (
              <Link
                to="/locations"
                className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline transition-all duration-200"
              >
                Open Locations
              </Link>
            )}
          </div>
        ) : (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
            Ask the owner to fix this.
          </p>
        )}
      </div>
    </div>
  );
}
